const apiKey = import.meta.env.VITE_FIREBASE_API_KEY?.trim();
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID?.trim();

export const firebaseConfigured = Boolean(apiKey && projectId);
const firestoreBase = projectId
  ? `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents`
  : '';
const sessionKey = 'menu-finder-firebase-session';

function decodeValue(value) {
  if (!value) return null;
  if ('stringValue' in value) return value.stringValue;
  if ('booleanValue' in value) return value.booleanValue;
  if ('integerValue' in value) return Number(value.integerValue);
  if ('doubleValue' in value) return Number(value.doubleValue);
  if ('timestampValue' in value) return value.timestampValue;
  if ('nullValue' in value) return null;
  if ('arrayValue' in value) return (value.arrayValue.values || []).map(decodeValue);
  if ('mapValue' in value) return decodeFields(value.mapValue.fields || {});
  return null;
}

function decodeFields(fields = {}) {
  return Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, decodeValue(value)]));
}

function encodeValue(value) {
  if (value === null || value === undefined) return { nullValue: null };
  if (Array.isArray(value)) return { arrayValue: { values: value.map(encodeValue) } };
  if (typeof value === 'boolean') return { booleanValue: value };
  if (typeof value === 'number') {
    return Number.isInteger(value) ? { integerValue: String(value) } : { doubleValue: value };
  }
  if (typeof value === 'object') return { mapValue: { fields: encodeFields(value) } };
  return { stringValue: String(value) };
}

function encodeFields(record) {
  return Object.fromEntries(
    Object.entries(record)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, encodeValue(value)]),
  );
}

function decodeDocument(document) {
  const nameParts = document.name?.split('/') || [];
  return { id: nameParts.at(-1), ...decodeFields(document.fields || {}) };
}

async function request(url, options = {}) {
  const response = await fetch(url, options);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = payload?.error?.message || `Firebase request failed (${response.status})`;
    throw new Error(message.replaceAll('_', ' ').toLowerCase());
  }
  return payload;
}

export async function signInAdmin(email, password) {
  if (!firebaseConfigured) throw new Error('Firebase is not configured');
  const payload = await request(
    `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${encodeURIComponent(apiKey)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, returnSecureToken: true }),
    },
  );

  const session = {
    idToken: payload.idToken,
    refreshToken: payload.refreshToken,
    uid: payload.localId,
    email: payload.email,
    expiresAt: Date.now() + Number(payload.expiresIn || 3600) * 1000 - 60_000,
  };
  localStorage.setItem(sessionKey, JSON.stringify(session));
  return session;
}

async function refreshSession(session) {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: session.refreshToken,
  });
  const payload = await request(
    `https://securetoken.googleapis.com/v1/token?key=${encodeURIComponent(apiKey)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body },
  );
  const refreshed = {
    ...session,
    idToken: payload.id_token,
    refreshToken: payload.refresh_token || session.refreshToken,
    uid: payload.user_id,
    expiresAt: Date.now() + Number(payload.expires_in || 3600) * 1000 - 60_000,
  };
  localStorage.setItem(sessionKey, JSON.stringify(refreshed));
  return refreshed;
}

export async function getAdminSession() {
  const raw = localStorage.getItem(sessionKey);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw);
    return session.expiresAt > Date.now() ? session : await refreshSession(session);
  } catch {
    localStorage.removeItem(sessionKey);
    return null;
  }
}

export function signOutAdmin() {
  localStorage.removeItem(sessionKey);
}

export async function fetchActiveRestaurants() {
  if (!firebaseConfigured) throw new Error('Firebase is not configured');
  const payload = await request(`${firestoreBase}:runQuery`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      structuredQuery: {
        from: [{ collectionId: 'restaurants' }],
        where: {
          fieldFilter: {
            field: { fieldPath: 'active' },
            op: 'EQUAL',
            value: { booleanValue: true },
          },
        },
      },
    }),
  });
  return payload
    .filter((row) => row.document)
    .map((row) => decodeDocument(row.document))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export async function fetchAllRestaurants(session) {
  const payload = await request(`${firestoreBase}/restaurants?pageSize=500`, {
    headers: { Authorization: `Bearer ${session.idToken}` },
  });
  return (payload.documents || []).map(decodeDocument).sort((a, b) => (a.order || 0) - (b.order || 0));
}

export async function saveRestaurant(restaurant, session) {
  const id = restaurant.id.trim();
  const record = {
    name: restaurant.name?.trim() || '',
    nameAr: restaurant.nameAr?.trim() || '',
    aliases: Array.isArray(restaurant.aliases) ? restaurant.aliases : [],
    menuUrl: restaurant.menuUrl?.trim() || '',
    location: restaurant.location?.trim() || '',
    phone: restaurant.phone?.trim() || '',
    category: restaurant.category?.trim() || '',
    description: restaurant.description?.trim() || '',
    coverImage: restaurant.coverImage?.trim() || '',
    active: restaurant.active !== false,
    order: Number(restaurant.order || 0),
    updatedAt: new Date().toISOString(),
  };
  const payload = await request(`${firestoreBase}/restaurants/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.idToken}`,
    },
    body: JSON.stringify({ fields: encodeFields(record) }),
  });
  return decodeDocument(payload);
}

export async function deleteRestaurant(id, session) {
  await request(`${firestoreBase}/restaurants/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${session.idToken}` },
  });
}
